import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Intercepts the Android back button (popstate) when the user is on a root
 * screen (Landing or Dashboard) and shows a native-style "Exit BrimiAI
 * ScienceLab?" confirmation dialog. Confirming exits the TWA / closes the
 * tab; cancelling keeps them in place.
 *
 * Inside chapters, mock tests, subscribe, privacy, etc. the back button
 * behaves normally (this component is only mounted on Dashboard + Landing).
 */
export default function BackButtonGuard() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const isRoot = ["/", "/dashboard"].includes(location.pathname);
    if (!isRoot) return;

    // seed one phantom entry so the first back-press hits us instead of leaving
    window.history.pushState({ backGuard: true }, "");

    const onPop = () => {
      // put another phantom so subsequent back-presses also stay caught
      window.history.pushState({ backGuard: true }, "");
      setOpen(true);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [location.pathname]);

  const handleExit = () => {
    setOpen(false);
    // remove the phantom, then close the window / TWA
    try { window.history.go(-2); } catch (_) { /* noop */ }
    setTimeout(() => {
      try { window.close(); } catch (_) { /* noop */ }
    }, 50);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="brut-card rounded-none border-2 border-[#0F1120] bg-white" data-testid="exit-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-black text-xl tracking-tight">
            Exit BrimiAI ScienceLab?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-700">
            Your progress is saved. You can come back any time to continue where you left off.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            className="brut-btn rounded-none bg-white text-[#0F1120] hover:bg-white"
            data-testid="exit-cancel-btn">
            Stay
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleExit}
            className="brut-btn rounded-none bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]"
            data-testid="exit-confirm-btn">
            Exit App
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
